import React from 'react';
import { compose, branch, renderComponent, setDisplayName } from 'react-recompose';
import partition from 'lodash/partition';
import get from 'lodash/get';
import omit from 'lodash/omit';
import Message from '../Message';

/**
 * Graphql props function that exposes errors in `data.error.graphQLErrors`
 * associated with the given query as `data.warnings`, and removes the
 * `data.error` object unless there are other errors (graphql or network).
 * Use with `errorPolicy: 'all'` in graphql() options.
 *
 * Example:

 graphql(query, {
      options: props => ({
        errorPolicy: 'all',
        variables: {
          first: 25,
        },
      }),
      props: compose(
        queryErrorsToWarnings('searchColleges'),
        loadMorePaginationProp(query),
      ),
    })

 * @param queryNames  an array of graphql query names e.g. 'searchColleges'
 * @returns {function}  the function to be used as 'props' function for graphql()
 */
export function queryErrorsToWarnings(queryNames) {
  return (props) => {
    const { data, ...rest } = props;
    if (get(data, 'error.graphQLErrors')) {
      const { error: { graphQLErrors, networkError } } = data;
      const [queryErrors, otherErrors] = partition(
        graphQLErrors,
        // select errors associated with specific data nodes in the selected query - excludes general query errors
        (error) => error.path && error.path.length > 1 && error.path.find((path) => queryNames.includes(path)),
      );
      if (queryErrors.length) {
        const dropError = !otherErrors.length && !networkError;
        return {
          ...rest,
          data: {
            ...(dropError ? omit(data, 'error') : data),
            warnings: queryErrors,
          },
        };
      }
    }
    return props;
  };
}

export default compose(
  setDisplayName('GraphQLError'),
  branch(
    ({ data }) => data && data.error,
    renderComponent(({ data }) => (<Message error header="Oops, something went wrong" content={data.error.message} />)),
  ),
);
